import { defineStore } from 'pinia'
import { errorMessage } from '@/services/http'
import { catalogService } from '@/services/catalog'
import { filesService } from '@/services/files'
import { FALLBACK_MATERIAL } from '@/constants/materials'
import { useMaterialsStore } from './materials'
import { renderPriceKey } from '@/constants/pricing'
import type { PriceKey } from '@/constants/pricing'
import type { Quality } from '@/constants/render'
import type { SelectOption } from '@/types/ui'
import type { CatalogEntry, MaterialCatalogEntry, Price } from '@/types/api'

interface CatalogState {
  materials: MaterialCatalogEntry[]
  scenes: CatalogEntry[]
  prices: Price[]
  loading: boolean
  ready: boolean
  error: string | null
}

export const useCatalogStore = defineStore('catalog', {
  state: (): CatalogState => ({
    materials: [],
    scenes: [],
    prices: [],
    loading: false,
    ready: false,
    error: null,
  }),

  getters: {
    materialOptions: (state): SelectOption[] => [
      ...state.materials.map((m) => ({
        value: String(m.id),
        label: m.label,
        image: filesService.previewUrl(m.preview_file_id),
      })),
      ...useMaterialsStore().items.map((m) => ({
        value: m.id,
        label: m.name,
        image: filesService.previewUrl(m.preview_file_id),
      })),
    ],

    materialById: (state) => (id: number): MaterialCatalogEntry | null =>
      state.materials.find((m) => m.id === id) ?? null,

    defaultMaterial: (state): string => {
      const preferred =
        state.materials.find((m) => m.name === FALLBACK_MATERIAL) ?? state.materials[0]
      return preferred ? String(preferred.id) : ''
    },

    sceneOptions: (state): readonly SelectOption[] =>
      state.scenes.map((s) => ({ value: String(s.id), label: s.label })),

    hasScenes: (state): boolean => state.scenes.length > 0,

    priceOf: (state) => (key: PriceKey): number | null =>
      state.prices.find((p) => p.key === key)?.credits ?? null,

    renderCost(): (quality: Quality) => number | null {
      return (quality: Quality) => this.priceOf(renderPriceKey(quality))
    },
  },

  actions: {
    async fetchOnce(): Promise<void> {
      if (this.ready || this.loading) return
      this.loading = true
      this.error = null
      try {
        const [catalog, prices] = await Promise.all([
          catalogService.blender(),
          catalogService.pricing(),
        ])
        this.materials = catalog.materials
        this.scenes = catalog.scenes
        this.prices = prices
        this.ready = true
      } catch (e) {
        this.error = errorMessage(e)
      } finally {
        this.loading = false
      }
    },
  },
})
